import { cineamoApiClient } from '../../apiClient';
import type { FetchClientResponse } from '../../fetch-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { CinemaDto } from '../../models/cinema/CinemaDto.types';
import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { MovieDto } from '../../models/movie/v2/MovieDto.types';
import { ShowingDto, ShowingInputDto } from '../../models/showing/ShowingDto.types';
import { ShowingListResponse, ShowingTagListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

import { ShowingApiEmbed } from './showing-api.embed';
import { ShowingApiGroup } from './showing-api.group';

// <------------------------ Showings ------------------------>

// Map embed enum to ShowingDto embed keys and types
type EmbedMap = {
    [ShowingApiEmbed.Cinema]: { cinema: CinemaDto };
    [ShowingApiEmbed.CinemaRoom]: { cinemaRoom: CinemaRoomDto };
};

/** The embeds a `getShowings` caller actually asked for, resolved from its `embed` array. */
type RequestedShowingEmbeds<Q extends GetShowingsQuery> = NonNullable<ShowingDto['_embedded']> &
    Pick<EmbedMap, Extract<NonNullable<Q['embed']>[number], keyof EmbedMap>>;

export type GetShowingsQuery = {
    contentId?: string | number;
    cinemas?: number[] | string[];
    cinemaChainIds?: number[];
    startDatetime?: string;
    endDatetime?: string;
    /** Filter by movie id (Content.movie). */
    movieId?: MovieDto['id'];
    name?: string;
    longitude?: number;
    latitude?: number;
    distance?: number;
    isRegularShowtime?: boolean;
    isEventRequest?: boolean;
    isThreeDimensional?: boolean;
    isImax?: boolean;
    isDolbyAtmos?: boolean;
    isDolbyVision?: boolean;
    isDolbyCinema?: boolean;
    is4DX?: boolean;
    isScreenX?: boolean;
    isHFR?: boolean;
    isLive?: boolean;
    isDbox?: boolean;
    isInfinityVision?: boolean;
    isOriginalLanguage?: boolean;
    isSubtitled?: boolean;
    hasMovie?: boolean;
    group?: ShowingApiGroup;
    embed?: ShowingApiEmbed[];
} & UrlQueryParameter;

/**
 * This endpoint returns a list of showings.
 * Return type is automatically inferred from embed parameter.
 *
 * @example
 * // Without embeds - all optional
 * getShowings({ cinemas: [1] });
 *
 * // With cinema embed - cinema required in return type
 * getShowings({ embed: [ShowingApiEmbed.Cinema] });
 *
 * @param query
 * @returns {ApiResponse<ShowingListResponse>} with typed ShowingDto
 */
export async function getShowings<Q extends GetShowingsQuery>(
    query: Q
): Promise<ApiResponse<unknown, ShowingListResponse<RequestedShowingEmbeds<Q>>>> {
    return cineamoApiClient
        .get<ApiResponse<unknown, ShowingListResponse<RequestedShowingEmbeds<Q>>>>(`/showings${buildUrlQuery(query)}`)
        .then((response) => response.data);
}

export type GetShowingDayCountsQuery = {
    cinemaIds: number[];
    /** Narrows to cinemas of these chains; a chain tenant is scoped server-side regardless. */
    cinemaChainIds?: number[];
    contentId?: number;
    /** Inclusive lower bound on the showing start, ISO 8601. */
    startDatetime: string;
    /** Exclusive upper bound on the showing start, ISO 8601; at most 366 days after startDatetime. */
    endDatetime: string;
    /** IANA zone the days are keyed in; the API defaults to Europe/Berlin. */
    timeZone?: string;
};

export type ShowingDayCountsResponse = {
    timeZone: string;
    /** Always 0 — a showing counts for the calendar day it starts on. Reported, not assumed. */
    dayStartHour: number;
    /** `YYYY-MM-DD` day => showings; days without showings are absent. */
    counts: Record<string, number>;
};

/**
 * Showings per calendar day for the range, in one request — the calendar bar's badges (CIN-11744).
 * Same visibility rules as `getShowings`, so a day's count equals that day's `_total_items`.
 */
export async function getShowingDayCounts(query: GetShowingDayCountsQuery): Promise<ShowingDayCountsResponse> {
    return cineamoApiClient
        .get<ShowingDayCountsResponse>(`/showings/day-counts${buildUrlQuery(query)}`)
        .then((response) => response.data);
}

// <------------------------ Showing ------------------------>

/**
 * This endpoint returns a single showing.
 *
 * @example
 * // Get a specific showing by content and request id:
 * getShowingByRequestId();
 *
 * @param showingId
 * @returns {ApiResponse<ShowingDto>}
 */

export async function getShowingById(showingId: number | string): Promise<ApiResponse<ShowingDto>> {
    return cineamoApiClient.get<ApiResponse<ShowingDto>>(`/showings/${showingId}`).then((response) => response.data);
}

export async function createShowing(showing: ShowingInputDto): Promise<ApiResponse<ShowingDto>> {
    return cineamoApiClient.post<ApiResponse<ShowingDto>>('/showings', showing).then((response) => response.data);
}

export async function updateShowing(showing: ShowingInputDto): Promise<ApiResponse<ShowingDto>> {
    return cineamoApiClient
        .patch<ApiResponse<ShowingDto>>(`/showings/${showing.id}`, showing)
        .then((response) => response.data);
}

export async function deleteShowing(showing: ShowingInputDto): Promise<FetchClientResponse<ApiResponse<ShowingDto>>> {
    return cineamoApiClient.delete<ApiResponse<ShowingDto>>(`/showings/${showing.id}`).then((response) => response);
}

export async function addTagToShowing(
    showingId: number,
    showingTagId: number
): Promise<FetchClientResponse<ApiResponse<ShowingDto>>> {
    return cineamoApiClient
        .put<ApiResponse<ShowingDto>>(`/showings/${showingId}/showing-tags/${showingTagId}`)
        .then((response) => response);
}

export async function removeTagFromShowing(showingId: number, showingTagId: number): Promise<boolean> {
    return cineamoApiClient
        .delete(`/showings/${showingId}/showing-tags/${showingTagId}`)
        .then((response) => response.status === 204);
}

// <------------------------ Cinema Future Showings ------------------------>

export type GetCinemaFutureShowingsQuery = {
    showingTagIds?: number[] | string[];
    hasShowingTags?: boolean;
    /** Filter by movie id (Content.movie). */
    movieId?: MovieDto['id'];
    embed?: 'cinemaRoom'[];
} & UrlQueryParameter;

/**
 * Returns all future showings for a cinema.
 * This endpoint includes showingTagIds on each showing, like /showings.
 * Backend hardcodes perPage: 5000 and startDatetime: midnight today on the cinema's clock.
 */
export async function getCinemaFutureShowings(
    cinemaId: CinemaDto['id'] | string,
    query?: GetCinemaFutureShowingsQuery
): Promise<ApiResponse<unknown, ShowingListResponse>> {
    return cineamoApiClient
        .get<ApiResponse<unknown, ShowingListResponse>>(`/cinemas/${cinemaId}/showings-future${buildUrlQuery(query)}`, {
            credentials: 'same-origin'
        })
        .then((response) => response.data);
}

// <------------------------ Showing Tags ------------------------>

type GetShowingTagsParams = UrlQueryParameter;
export async function getShowingTags(
    showingId: ShowingDto['id'] | string,
    params?: GetShowingTagsParams
): Promise<ApiResponse<ShowingTagListResponse>> {
    return await cineamoApiClient
        .get<ApiResponse<ShowingTagListResponse>>(`/showings/${showingId}/tags${buildUrlQuery(params)}`, {
            credentials: 'same-origin'
        })
        .then((response) => response.data);
}
